import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Building2, 
  Users, 
  FileText, 
  CheckCircle, 
  LogOut,
  Plus,
  Settings
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des statistiques");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const statCards = [
    {
      title: "Entreprises",
      value: stats?.total_companies || 0,
      subtitle: `${stats?.active_companies || 0} actives`,
      icon: Building2,
      color: "bg-sky-600",
      link: "/admin/companies",
    },
    {
      title: "Utilisateurs",
      value: stats?.total_users || 0,
      subtitle: "Total inscrits",
      icon: Users,
      color: "bg-orange-500",
      link: "/admin/users",
    },
    {
      title: "Dossiers",
      value: stats?.total_claims || 0,
      subtitle: "Total créés",
      icon: FileText,
      color: "bg-green-500",
    },
    {
      title: "Terminés",
      value: stats?.completed_claims || 0,
      subtitle: "Dossiers complétés",
      icon: CheckCircle,
      color: "bg-purple-500",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                Administration
              </h1>
              <p className="text-slate-500 text-sm">
                Bonjour, {user?.first_name} {user?.last_name}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="h-10"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <Card
                key={index}
                className={`cursor-pointer transition-shadow hover:shadow-md ${card.link ? "" : "cursor-default"}`}
                onClick={() => card.link && navigate(card.link)}
                data-testid={`stat-card-${card.title.toLowerCase()}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-slate-500 uppercase tracking-wide">
                        {card.title}
                      </p>
                      <p className="text-3xl font-bold text-slate-900 mt-1">
                        {loading ? "..." : card.value}
                      </p>
                      <p className="text-sm text-slate-400 mt-1">{card.subtitle}</p>
                    </div>
                    <div className={`${card.color} rounded-lg p-3`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Actions */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-xl">Actions rapides</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Button
                onClick={() => navigate("/admin/companies")}
                className="h-16 bg-sky-600 hover:bg-sky-700 justify-start"
                data-testid="quick-action-companies"
              >
                <Building2 className="w-6 h-6 mr-3" />
                <div className="text-left">
                  <div className="font-semibold">Gérer les entreprises</div>
                  <div className="text-xs opacity-80">Créer, modifier, désactiver</div>
                </div>
              </Button>
              
              <Button
                onClick={() => navigate("/admin/users")}
                className="h-16 bg-orange-500 hover:bg-orange-600 justify-start"
                data-testid="quick-action-users"
              >
                <Users className="w-6 h-6 mr-3" />
                <div className="text-left">
                  <div className="font-semibold">Gérer les utilisateurs</div>
                  <div className="text-xs opacity-80">Voir tous les comptes</div>
                </div>
              </Button>

              <Button
                variant="outline"
                onClick={() => fetchStats()}
                className="h-16 justify-start"
                data-testid="quick-action-refresh"
              >
                <Settings className="w-6 h-6 mr-3" />
                <div className="text-left">
                  <div className="font-semibold">Actualiser les données</div>
                  <div className="text-xs text-slate-500">Recharger les statistiques</div>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Info */}
        <Card>
          <CardContent className="py-6">
            <div className="text-center text-slate-500">
              <p className="text-sm">
                Plateforme de gestion des abonnements AquaReport
              </p>
              <p className="text-xs mt-2">
                Les entreprises créent leurs propres techniciens et gèrent leurs dossiers
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminDashboard;
